import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  MoreHorizontal,
  Languages,
  MoveVertical,
  Trash2,
  Pencil,
  ListRestart,
} from "lucide-react";
import { CategorySectionProps } from "./types";
import { MenuItem, Menu } from "@/types/menu";
import EditableText from "./EditableText";
import DraggableMenuItemCard from "./DraggableMenuItemCard";
import MenuItemEditDialog from "./MenuItemEditDialog";
import TranslateSectionDialog from "./TranslateSectionDialog";
import ReorderSectionsDialog from "../ReorderSectionsDialog";
import ReorderMenuItemsDialog from "./ReorderMenuItemsDialog";
import { createClient } from "@/lib/supabase/client";
import useMenuStore from "@/store/menuStore";
import { CreditWarningDialog } from "./CreditWarningDialog";
import CategoryEditDialog from "./CategoryEditDialog";
import { useTranslations } from "next-intl";

export function CategorySection({
  category,
  language,
  id,
  onUpdate,
  onDelete,
  items,
  isDropTarget,
  activeId,
  overId,
  isEditable = false,
  menu,
  menuId,
}: CategorySectionProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: category.id,
    data: {
      type: "category",
      categoryId: category.id,
    },
  });

  const t = useTranslations("CategorySection");
  const [editingField, setEditingField] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [showTranslateDialog, setShowTranslateDialog] = useState(false);
  const [showReorderDialog, setShowReorderDialog] = useState(false);
  const [showReorderItemsDialog, setShowReorderItemsDialog] = useState(false);
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [pendingField, setPendingField] = useState<
    "name" | "description" | null
  >(null);
  const [newItem, setNewItem] = useState<MenuItem | null>(null);
  const { toast } = useToast();

  const handleDelete = () => {
    if (!onDelete) return;
    onDelete(category);
    setShowDeleteDialog(false);
  };

  const handleAddItem = () => {
    // Create empty names object for all supported languages
    const defaultNames = menu.supportedLanguages.reduce((acc, lang) => {
      return { ...acc, [lang]: "" };
    }, {});

    const newItemTemplate: MenuItem = {
      id: `item-${Date.now()}`,
      name: defaultNames,
      price: {
        amount: 0,
      },
      category: category.id,
    };
    setNewItem(newItemTemplate);
    setShowNewItemDialog(true);
  };

  const handleNewItemSave = (item: MenuItem) => {
    if (!onUpdate) return;

    const updatedCategory = {
      ...category,
      items: [...category.items, item],
    };

    onUpdate(updatedCategory);
    setNewItem(null);
    setShowNewItemDialog(false);

    toast({
      title: t("itemAdded"),
      description: t("itemAddedDescription"),
    });
  };

  const handleNewItemCancel = () => {
    setNewItem(null);
    setShowNewItemDialog(false);
  };

  // Determine if we're dragging from a different category
  const isDraggingFromDifferentCategory =
    isOver && activeId && !items.find((item) => item.id === activeId);

  const handleItemUpdate = (updatedItem: MenuItem) => {
    if (!onUpdate) return;

    // Handle item deletion
    if ("_delete" in updatedItem) {
      const updatedCategory = {
        ...category,
        items: items.filter((item) => item.id !== updatedItem.id),
      };
      onUpdate(updatedCategory);
      return;
    }

    // Handle normal updates
    const updatedCategory = {
      ...category,
      items: items.map((item) =>
        item.id === updatedItem.id ? updatedItem : item
      ),
    };
    onUpdate(updatedCategory);
  };

  // Add credit check for description editing
  const handleDescriptionSave = async (value: string) => {
    const updatedCategory = {
      ...category,
      description: {
        ...(category.description || {}),
        [language]: value,
      },
    };

    if (onUpdate) {
      await onUpdate(updatedCategory);
      setEditingField(null);
    }
  };

  const handleEdit = (field: "name" | "description") => {
    if (field === "name") {
      // Only show dialog for multilingual menus
      if (menu.supportedLanguages.length > 1) {
        setShowEditDialog(true);
      } else {
        setEditingField(field);
      }
    } else {
      setEditingField(field);
    }
  };

  return (
    <>
      <section
        id={id}
        className={cn(
          "scroll-mt-[120px] lg:scroll-mt-[100px]",
          isDropTarget &&
            "bg-muted/50 ring-1 ring-muted-foreground/20 rounded-lg",
          "transition-all duration-200 ease-out"
        )}
        aria-labelledby={`category-heading-${id}`}
      >
        <div className="pt-4 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {menu.supportedLanguages.length === 1 ? (
                <h2 id={`category-heading-${id}`}>
                  <EditableText
                    value={category.name[language] || ""}
                    onSave={async (value) => {
                      const updatedCategory = {
                        ...category,
                        name: {
                          ...category.name,
                          [language]: value,
                        },
                      };
                      if (onUpdate) {
                        await onUpdate(updatedCategory);
                        setEditingField(null);
                      }
                    }}
                    isEditing={editingField === "name"}
                    onEdit={() => handleEdit("name")}
                    onCancel={() => setEditingField(null)}
                    className="text-lg font-semibold"
                    isEditable={isEditable}
                    showCreditWarning={false}
                    supportedLanguages={menu.supportedLanguages}
                    currentLanguage={language}
                  />
                </h2>
              ) : (
                <h2
                  id={`category-heading-${id}`}
                  className={cn(
                    isEditable
                      ? "group/edit relative inline-flex items-center max-w-full"
                      : "",
                    "text-lg font-semibold"
                  )}
                  onClick={isEditable ? () => handleEdit("name") : undefined}
                >
                  <div
                    className={cn(
                      isEditable
                        ? "group-hover/edit:bg-muted/50 cursor-pointer"
                        : "",
                      "rounded px-1 -mx-1 py-0.5 transition-colors"
                    )}
                  >
                    {category.name[language] || ""}
                  </div>
                  {isEditable && (
                    <button
                      onClick={() => handleEdit("name")}
                      className="opacity-70 hover:opacity-100 ml-2 p-1 hover:bg-muted rounded transition-all"
                      aria-label={`Edit ${
                        category.name[language] || "category"
                      } name`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </h2>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isEditable && (
                <>
                  <Button
                    onClick={handleAddItem}
                    variant="outline"
                    size="sm"
                    className="gap-1"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {t("addItem")}
                  </Button>

                  {/* Only show dropdown if there are menu options available */}
                  {((menu.supportedLanguages.length > 1 &&
                    Object.keys(menu.title || {}).some(
                      (lang) => lang !== language && menu.title?.[lang]
                    )) ||
                    items.length > 1 ||
                    menu.categories.length > 1) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("moreActions")}
                        >
                          <MoreHorizontal
                            className="h-4 w-4"
                            aria-hidden="true"
                          />
                          <span className="sr-only">{t("moreActions")}</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {menu.supportedLanguages.length > 1 &&
                          /* Check if any translation exists in this menu */
                          Object.keys(menu.title || {}).some(
                            (lang) => lang !== language && menu.title?.[lang]
                          ) && (
                            <DropdownMenuItem
                              onClick={() => setShowTranslateDialog(true)}
                            >
                              <Languages
                                className="h-4 w-4 mr-2"
                                aria-hidden="true"
                              />
                              {t("translateSection")}
                            </DropdownMenuItem>
                          )}
                        {items.length > 1 && (
                          <DropdownMenuItem
                            onClick={() => setShowReorderItemsDialog(true)}
                          >
                            <ListRestart
                              className="h-4 w-4 mr-2"
                              aria-hidden="true"
                            />
                            {t("reorderMenuItems")}
                          </DropdownMenuItem>
                        )}
                        {menu.categories.length > 1 && (
                          <DropdownMenuItem
                            onClick={() => setShowReorderDialog(true)}
                          >
                            <MoveVertical
                              className="h-4 w-4 mr-2"
                              aria-hidden="true"
                            />
                            {t("reorderSection")}
                          </DropdownMenuItem>
                        )}
                        {menu.categories.length > 1 && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setShowDeleteDialog(true)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2
                                className="h-4 w-4 mr-2"
                                aria-hidden="true"
                              />
                              {t("deleteSection")}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              )}
            </div>
          </div>
          {(category.description || editingField === "description") && (
            <EditableText
              value={category.description?.[language] || ""}
              onSave={handleDescriptionSave}
              isEditing={editingField === "description"}
              onEdit={() => handleEdit("description")}
              onCancel={() => setEditingField(null)}
              className="mt-1 text-sm text-muted-foreground"
              textComponent="textarea"
              isEditable={isEditable}
              requiredCredits={menu.supportedLanguages.length - 1}
              showCreditWarning={false}
              supportedLanguages={menu.supportedLanguages}
              currentLanguage={language}
            />
          )}
        </div>
        <div
          ref={setNodeRef}
          className={cn(
            "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-4 py-4 relative",
            "transition-all duration-300 ease-in-out",
            items.length === 0 && "min-h-[100px]",
            items.length === 0 &&
              (isDropTarget || isOver) &&
              "min-h-[200px] bg-muted/30 scale-[1.02]",
            items.length === 0 &&
              !(isDropTarget || isOver) &&
              "bg-muted/20 rounded-lg"
          )}
          role="list"
          aria-label={`Items in ${category.name[language] || "this category"}`}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={rectSortingStrategy}
          >
            {items.map((item) => {
              const isItemOver = overId === item.id;
              const isSelf = activeId === item.id;
              return (
                <div key={item.id} className="relative" role="listitem">
                  {isItemOver && !isSelf && (
                    <div
                      className="absolute inset-0 bg-muted/50 rounded-lg border border-muted-foreground/20"
                      aria-hidden="true"
                    />
                  )}
                  <DraggableMenuItemCard
                    item={item}
                    language={language}
                    categoryId={category.id}
                    onUpdate={handleItemUpdate}
                    isEditable={isEditable}
                    supportedLanguages={menu.supportedLanguages}
                    isNewItem={false}
                    menuId={menuId}
                    menu={menu}
                  />
                </div>
              );
            })}
            {items.length === 0 && (isDropTarget || isOver) && (
              <div
                className="col-span-2 h-full flex items-center justify-center text-muted-foreground"
                aria-hidden="true"
              >
                <div className="text-center">
                  <div className="text-lg font-medium">{t("dropHere")}</div>
                  <div className="text-sm">{t("releaseToAdd")}</div>
                </div>
              </div>
            )}
            {isDraggingFromDifferentCategory && !overId && (
              <div
                className="bg-muted/50 rounded-lg border border-muted-foreground/20 h-[200px]"
                aria-hidden="true"
              />
            )}
          </SortableContext>
        </div>
      </section>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("delete")} &ldquo;
              {category.name[language] || category.name.japanese}
              &rdquo;
            </AlertDialogTitle>
            <AlertDialogDescription>
              {items.length > 0
                ? t("deleteConfirmationWithItems", {
                    name: category.name[language] || category.name.japanese,
                    count: items.length,
                    itemLabel: items.length === 1 ? t("item") : t("items"),
                  })
                : t("deleteConfirmation", {
                    name: category.name[language] || category.name.japanese,
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {newItem && (
        <MenuItemEditDialog
          item={newItem}
          language={language}
          isOpen={showNewItemDialog}
          onClose={handleNewItemCancel}
          onUpdate={handleNewItemSave}
          onDelete={handleNewItemCancel}
          supportedLanguages={menu.supportedLanguages}
          isNewItem={true}
          categoryId={category.id}
          menuId={menuId}
          currency={menu.currency || "¥"}
          menu={menu}
        />
      )}

      <TranslateSectionDialog
        category={category}
        isOpen={showTranslateDialog}
        onClose={() => setShowTranslateDialog(false)}
        onUpdate={onUpdate || (() => {})}
        supportedLanguages={menu.supportedLanguages}
        language={language}
      />

      <ReorderSectionsDialog
        menu={menu}
        isOpen={showReorderDialog}
        onClose={() => setShowReorderDialog(false)}
        onUpdate={async (updatedMenu: Menu) => {
          try {
            const supabase = createClient();
            await useMenuStore
              .getState()
              .updateTranslation(menuId, updatedMenu, supabase);
            toast({
              title: t("menuUpdated"),
              description: t("menuUpdatedDescription"),
            });
          } catch (error) {
            console.error("Error updating menu:", error);
            toast({
              variant: "destructive",
              title: t("error"),
              description: t("reorderError"),
            });
          }
        }}
        language={language}
      />

      <ReorderMenuItemsDialog
        category={category}
        isOpen={showReorderItemsDialog}
        onClose={() => setShowReorderItemsDialog(false)}
        onUpdate={onUpdate || (() => {})}
        language={language}
        currency={menu.currency || "¥"}
      />

      <CreditWarningDialog
        open={showCreditDialog}
        onOpenChange={setShowCreditDialog}
        requiredCredits={menu.supportedLanguages.length - 1}
        supportedLanguages={menu.supportedLanguages}
        currentLanguage={language}
        onProceed={(selectedLanguages: string[]) => {
          setShowCreditDialog(false);
          if (selectedLanguages.length > 0 && pendingField) {
            setEditingField(pendingField);
          }
          setPendingField(null);
        }}
      />

      <CategoryEditDialog
        category={category}
        language={language}
        isOpen={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onUpdate={onUpdate || (() => {})}
        supportedLanguages={menu.supportedLanguages}
      />
    </>
  );
}

export default CategorySection;
